import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import format from "date-fns/format";
import { it } from "date-fns/locale";
import Intestazione from "../../assets/intestazione-Giuvi-3.png";

// --- Blueprint per il report documenti dei dipendenti ---
// Genera un PDF con l'intestazione aziendale e l'elenco dei soli documenti
// critici (mancanti, scaduti o in scadenza). I dati arrivano già filtrati per
// settore; l'ordinamento può essere per dipendente (raggruppato per settore)
// oppure per tipo di documento.

const borderColor = [161, 16, 0];

// Dati aziendali mostrati nel piè di pagina (allineati agli altri generatori).
const FOOTER_RIGA_1 =
  "GIUVI SRL - Via Belgirate, 15 - 20125 Milano (MI) - Tel. 02/97190315";
const FOOTER_RIGA_2 =
  "Cap.Soc. € 10.000,00 - PI/CF/Nr.Reg.Impr.MI IT07873720960 - REA MI-2010873 - info@giuvi.it";

// Colore del testo della colonna "Stato": lettura immediata del problema.
const COLORI_STATO = {
  Mancante: [239, 68, 68],
  Scaduto: [249, 115, 22],
  "In scadenza": [245, 158, 11],
};

const formatData = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : format(date, "dd/MM/yyyy");
};

async function loadImageAsDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const maxWidth = 800; // Larghezza sufficiente per un'intestazione A4
      let width = img.width;
      let height = img.height;

      // Ridimensiona se troppo grande
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Sfondo bianco (gestisce trasparenza PNG -> JPEG)
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Converti in JPEG compresso (qualità 0.7)
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Genera e scarica il report dei documenti critici dei dipendenti.
 *
 * @param {Object} options
 * @param {Array<{nome:string, settore:string, righe:Array<{tipoLabel:string, stato:string, statoLabel:string, scadenza:any}>}>} [options.utenti]
 *        Dipendenti da includere (già filtrati per settore), con i soli
 *        documenti mancanti, scaduti o in scadenza.
 * @param {string[]} [options.settori]  Settori inclusi, mostrati nel riepilogo
 * @param {"persona"|"documento"} [options.ordine]  Criterio di ordinamento
 * @param {string} [options.fileName]   Nome file di output
 */
export async function downloadDocumentiUtentiPdf({
  utenti = [],
  settori = [],
  ordine = "persona",
  fileName,
} = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  doc.setFont("helvetica", "normal");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginLeft = 15;
  const marginRight = 15;
  const marginBottom = 26;
  const primaRiga = 24; // prima riga utile sotto l'intestazione

  const headerImgData = await loadImageAsDataUrl(Intestazione);

  const addHeader = () => {
    if (headerImgData) {
      try {
        doc.addImage(headerImgData, "JPEG", marginLeft, 10, 45, 0);
      } catch (_) {
        // ignora errori immagine senza interrompere la generazione
      }
    }
    const textX = headerImgData ? marginLeft + 52 : marginLeft;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("Report Documenti Dipendenti", textX, 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(90);
    doc.text(
      `Documenti mancanti, scaduti o in scadenza - generato il ${format(
        new Date(),
        "dd/MM/yyyy HH:mm",
        { locale: it },
      )}`,
      textX,
      22,
    );
    doc.setTextColor(0);
  };

  const addFooter = (data) => {
    const pageCount = doc.getNumberOfPages();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(120);
    doc.text(FOOTER_RIGA_1, pageWidth / 2, pageHeight - 13, {
      align: "center",
    });
    doc.text(FOOTER_RIGA_2, pageWidth / 2, pageHeight - 9.5, {
      align: "center",
    });
    doc.text(
      `Pagina ${data.pageNumber} di ${pageCount}`,
      pageWidth - marginRight,
      pageHeight - 9.5,
      { align: "right" },
    );
    doc.setTextColor(0);
  };

  addHeader();

  let y = primaRiga;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - marginBottom) {
      doc.addPage();
      addHeader();
      y = primaRiga;
    }
  };

  // Sottotitolo con i criteri scelti per il report
  doc.setFontSize(9);
  doc.setTextColor(90);
  [
    `Settori inclusi: ${settori.length ? settori.join(", ") : "nessuno"}`,
    `Ordinamento: ${
      ordine === "documento" ? "per tipo di documento" : "per dipendente"
    }`,
    `Dipendenti con documenti da segnalare: ${utenti.length}`,
  ].forEach((riga) => {
    doc.text(riga, marginLeft, y);
    y += 5;
  });
  doc.setTextColor(0);
  y += 4;

  const scriviTitoloSezione = (testo) => {
    ensureSpace(16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(borderColor[0], borderColor[1], borderColor[2]);
    doc.text(testo, marginLeft, y);
    y += 3;
    doc.setLineWidth(0.3);
    doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
    doc.line(marginLeft, y, pageWidth - marginRight, y);
    y += 5;
    doc.setTextColor(0);
    doc.setDrawColor(0);
    doc.setFont("helvetica", "normal");
  };

  // Intestazione del singolo dipendente (una tabella per dipendente)
  const scriviTitoloUtente = (testo) => {
    ensureSpace(22);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(0);
    doc.text(testo, marginLeft, y);
    y += 4;
    doc.setFont("helvetica", "normal");
  };

  const scriviTabella = (head, body) => {
    autoTable(doc, {
      startY: y,
      head: [head],
      body,
      theme: "grid",
      styles: {
        fontSize: 8.5,
        cellPadding: 1.6,
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: { fillColor: borderColor, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      margin: {
        top: primaRiga,
        left: marginLeft,
        right: marginRight,
        bottom: marginBottom,
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const colore = COLORI_STATO[String(data.cell.raw)];
        if (colore) {
          data.cell.styles.textColor = colore;
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: (data) => {
        addHeader();
        addFooter(data);
      },
    });
    y = (doc.lastAutoTable?.finalY || y) + 8;
  };

  if (utenti.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(
      "Nessun documento da segnalare nei settori selezionati.",
      marginLeft,
      y,
    );
  } else if (ordine === "documento") {
    // Raggruppa per tipo di documento
    const perTipo = new Map();
    utenti.forEach((u) => {
      (u.righe || []).forEach((r) => {
        if (!perTipo.has(r.tipoLabel)) perTipo.set(r.tipoLabel, []);
        perTipo.get(r.tipoLabel).push({
          dipendente: u.nome || "—",
          settore: u.settore || "Nessun settore",
          stato: r.statoLabel,
          scadenza: formatData(r.scadenza),
        });
      });
    });

    Array.from(perTipo.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "it"))
      .forEach(([tipo, righe]) => {
        righe.sort(
          (a, b) =>
            a.settore.localeCompare(b.settore, "it") ||
            a.dipendente.localeCompare(b.dipendente, "it"),
        );
        scriviTitoloSezione(`${tipo} (${righe.length})`);
        scriviTabella(
          ["Dipendente", "Settore", "Stato", "Scadenza"],
          righe.map((r) => [r.dipendente, r.settore, r.stato, r.scadenza]),
        );
      });
  } else {
    // Raggruppa per settore, poi per dipendente
    const perSettore = new Map();
    utenti.forEach((u) => {
      const settore = u.settore || "Nessun settore";
      if (!perSettore.has(settore)) perSettore.set(settore, []);
      perSettore.get(settore).push(u);
    });

    Array.from(perSettore.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "it"))
      .forEach(([settore, lista]) => {
        scriviTitoloSezione(
          `${settore} (${lista.length} ${
            lista.length === 1 ? "dipendente" : "dipendenti"
          })`,
        );

        [...lista]
          .sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it"))
          .forEach((u) => {
            const body = [...(u.righe || [])]
              .sort((a, b) => a.tipoLabel.localeCompare(b.tipoLabel, "it"))
              .map((r) => [r.tipoLabel, r.statoLabel, formatData(r.scadenza)]);
            scriviTitoloUtente(u.nome || "—");
            scriviTabella(["Documento", "Stato", "Scadenza"], body);
          });
      });
  }

  // Riepilogo finale per stato
  if (utenti.length > 0) {
    const conteggi = { Mancante: 0, Scaduto: 0, "In scadenza": 0 };
    utenti.forEach((u) => {
      (u.righe || []).forEach((r) => {
        if (conteggi[r.statoLabel] !== undefined) conteggi[r.statoLabel] += 1;
      });
    });
    const totale =
      conteggi.Mancante + conteggi.Scaduto + conteggi["In scadenza"];

    scriviTitoloSezione("Riepilogo");
    scriviTabella(
      ["Stato", "Documenti"],
      [
        ["Mancante", conteggi.Mancante],
        ["Scaduto", conteggi.Scaduto],
        ["In scadenza", conteggi["In scadenza"]],
        ["Totale", totale],
      ],
    );
  }

  doc.save(
    fileName ||
      `report_documenti_dipendenti_${format(new Date(), "yyyy-MM-dd")}.pdf`,
  );
}
